import { getSession } from '@/lib/nextAuth';

type DataType = {
  [key: string]: string | number | boolean | Array<string | number | boolean> | undefined;
};

export const http = async <T>(
  path: string,
  options?: {
    /**
     * リクエストメソッド
     * Default:
     * 'GET'
     * */
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    /**
     * ヘッダー
     * Default:
     * headers = {
     *   'Content-Type': 'application/json',
     * }
     * */
    headers?: { [key: string]: string | undefined };
    /** 送信データ */
    body?: BodyInit;
    /** URLクエリ文字 */
    query?: DataType;
    /**
     * キャッシュ戦略
     * - 'no-store': キャッシュしない（常に最新データを取得）
     * - 'force-cache': 永続的にキャッシュ
     */
    cache?: 'no-store' | 'force-cache';
    /**
     * 再検証時間（秒）
     * - 数値を指定: ISRのrevalidate時間（例: 60 = 60秒間キャッシュ）
     * - 未指定: キャッシュしない（revalidate: 0）
     * Default: 0
     */
    revalidate?: number;
  },
): Promise<T> => {
  const {
    method = 'GET',
    body,
    query,
    headers = {
      'Content-Type': 'application/json',
    },
    cache,
    revalidate = 0,
  } = options ?? {};

  const session = await getSession();

  // テスト環境判定: HTTPヘッダーで判定
  const isTestEnvironment = (() => {
    try {
      // Next.js 13+ App Routerのheaders()関数を使用
      const { headers: nextHeaders } = require('next/headers');
      const headersList = nextHeaders();
      return headersList.get('x-test-environment') === 'true';
    } catch {
      // headers()が利用できない場合（Pages Routerなど）は false
      return false;
    }
  })();

  let url: URL;
  if (isTestEnvironment) {
    // 全てのAPIリクエストを動的モックAPIにリダイレクト
    // モックAPIが実際のAPI呼び出しのフォールバックを処理
    const mockPath = path.startsWith('/') ? path.substring(1) : path;
    url = new URL(`http://localhost:4012/api/test-mock/${mockPath}`);
  } else {
    url = new URL(process.env.NEXT_PUBLIC_API_BASE_URL as string);
    url.pathname = path;
  }

  if (query) {
    Object.keys(query).forEach((k) => {
      const queryValue = query[k];
      if (Array.isArray(queryValue)) {
        queryValue.forEach((v) => {
          url.searchParams.append(`${k}[]`, String(v));
        });
      } else if (queryValue != null) {
        url.searchParams.append(k, String(queryValue));
      }
    });
  }

  const res = await fetch(url.toString(), {
    method: method,
    body: body,
    headers: {
      Authorization: `Bearer ${session?.accessToken}`,
      ...headers,
    },
    cache,
    next: { revalidate: revalidate },
  });

  const responseJson = await res.json();

  if (responseJson.code !== 0) {
    return Promise.reject(responseJson);
  }

  return responseJson;
};
